import AppPage, {AppPageProps} from 'web-common/utilities/AppPage'
import kc from 'web-common/services/auth'
import {Box, Hidden, Drawer, IconButton, Grid} from '@mui/material'
import {SessionItemData} from 'views/assistant/components/sessions/Sessions'
import SessionsChatBox from './components/sessions/SessionsChatBox'
import SessionsSidebar from './components/sessions/SessionsSidebar'
import {v4 as UUIDv4} from 'uuid'
import Header from 'components/navigation/Header'
import {responsiveBP} from 'web-common/theme/AppTheme'
import HomeView from 'views/home/HomeView'
import appLogo from 'web-common/assets/img/logo.svg'
import {Link} from 'react-router-dom'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import SessionSkeleton from './components/sessions/SessionSkeleton'
import {AssistantChatService} from './services/AssistantChatService'
import AssistantEventBus from './components/timeline/AssistantEventBus'
import Loading from 'web-common/components/loading/Loading'
import CreateNewChatButton from './components/timeline/buttons/CreateNewChatButton'
import {EventBusRegistry} from '@fnd/timeline'
import AssistantDispatcher from './components/timeline/AssistantDispatcher'
import {PasswordCheck} from 'iconsax-reactjs'

interface AssistantViewRouteParams {
  sessionId?: string
}

interface AssistantViewProps extends AppPageProps<AssistantViewRouteParams> {}

interface AssistantViewState {
  activeSessions: SessionItemData[]
  demandSessions: SessionItemData[]
  selectedSession: SessionItemData
  isSessionsDrawerOpen: boolean
  loading: boolean
  initMessage?: string
}

class AssistantView extends AppPage<AssistantViewProps, AssistantViewState> {
  title = 'Assistant'
  static path = '/chat/:sessionId?'

  private chatService = AssistantChatService.shared(AssistantEventBus)
  private initSession = this.createSessionItem(this.getInitSessionId())

  private register: EventBusRegistry | undefined

  state: AssistantViewState = {
    activeSessions: [],
    demandSessions: [],
    selectedSession: this.initSession,
    isSessionsDrawerOpen: false,
    loading: true
  }

  async componentDidMount() {
    super.componentDidMount()

    AssistantEventBus.setCredentials({
      userId: kc.instance.subject!,
      token: kc.instance.token!
    })

    try {
      await AssistantEventBus.connect()
      await this.loadChatSessions()
    } catch (error) {
      console.error('Failed to connect and load sessions', error)
    } finally {
      this.setState({loading: false})
      this.registerAssistantDispatcher()
    }

    const queryParams = new URLSearchParams(this.props.location?.search ?? '')
    const initMessageFromQuery = queryParams.get('initialMessage')
    const initMessageFromState = this.props.location?.state?.message
    const userSearchQuery = initMessageFromQuery ?? initMessageFromState
    this.setState({initMessage: userSearchQuery ?? ''})
  }

  async componentDidUpdate(
    prevProps: AssistantViewProps,
    prevState: AssistantViewState
  ) {
    if (
      !this.state.loading &&
      this.state.activeSessions.length === 0 &&
      this.state.demandSessions.length === 0
    ) {
      await this.loadChatSessions()
    }

    const prevSessionId = prevProps.params.sessionId
    const currSessionId = this.props.params.sessionId

    if (
      currSessionId !== prevSessionId &&
      currSessionId !== this.state.selectedSession?.id
    ) {
      const sessionFound =
        this.state.activeSessions.find((s) => s.id === currSessionId) ??
        this.state.demandSessions.find((s) => s.id === currSessionId)

      if (sessionFound && sessionFound.id !== this.state.selectedSession.id) {
        this.setState({selectedSession: sessionFound})
      }
    }

    const prevSearch = prevProps.location.search
    const currSearch = this.props.location.search
    const prevQuery = prevProps.location.state?.message
    const currQuery = this.props.location.state?.message

    if (prevSearch !== currSearch || prevQuery !== currQuery) {
      const queryParams = new URLSearchParams(this.props.location.search)
      const initMessageFromQuery = queryParams.get('initialMessage')
      const initMessageFromState = this.props.location.state?.message
      const userSearchQuery = initMessageFromQuery ?? initMessageFromState
      this.setState({initMessage: userSearchQuery ?? ''})
    }
  }

  componentWillUnmount() {
    AssistantEventBus.disconnect()
  }

  complete(): void {
    this.register?.unregister()
  }

  registerAssistantDispatcher() {
    this.register = new AssistantDispatcher().register((event) => {
      const sessionId = event.data?.meta?.assignment?._id
      const action = event.data?.data?.metadata?.action
      const topic = event.data?.data?.metadata?.topic
      const demandId = event.data?.data?.metadata?.demandId

      this.setState(
        (prevState) => {
          const sessionExistsInActive = prevState.activeSessions.some(
            (session) => session.id === sessionId
          )

          let updatedActiveSessions = [...prevState.activeSessions]
          let updatedDemandSessions = [...prevState.demandSessions]
          let updatedSelectedSession = prevState.selectedSession

          if (sessionId && !sessionExistsInActive) {
            const newSession: SessionItemData = {
              id: sessionId,
              lastMessageDateCreated: Date.now()
            }

            updatedActiveSessions.push(newSession)

            if (!prevState.selectedSession || !prevState.selectedSession.id) {
              updatedSelectedSession = newSession
            }
          }

          const currentSession = updatedActiveSessions.find(
            (s) => s.id === sessionId
          )

          if (!currentSession) {
            return {
              ...prevState,
              activeSessions: updatedActiveSessions,
              demandSessions: updatedDemandSessions,
              selectedSession: updatedSelectedSession
            }
          }

          let updatedSession = {...currentSession}

          switch (action) {
            case 'CHAT_TOPIC_GENERATED':
              updatedSession.topic = topic
              updatedActiveSessions = updatedActiveSessions.map((session) =>
                session.id === sessionId ? updatedSession : session
              )
              break

            case 'DEMAND_CREATED':
              updatedSession.demandId = demandId
              updatedActiveSessions = updatedActiveSessions.filter(
                (session) => session.id !== sessionId
              )
              updatedDemandSessions = [
                ...updatedDemandSessions,
                updatedSession
              ].sort(
                (a, b) => b.lastMessageDateCreated - a.lastMessageDateCreated
              )
              break
          }

          if (prevState.selectedSession?.id === sessionId) {
            updatedSelectedSession = updatedSession
          }

          return {
            ...prevState,
            activeSessions: updatedActiveSessions,
            demandSessions: updatedDemandSessions,
            selectedSession: updatedSelectedSession
          }
        },
        () => {
          const sessionFound =
            this.state.activeSessions.find(
              (session) => session.id === this.state.selectedSession.id
            ) ||
            this.state.demandSessions.find(
              (session) => session.id === this.state.selectedSession.id
            )

          if (action !== 'DEMAND_CREATED') {
            this.props.navigate(`/chat/${sessionFound?.id ?? ''}`, {
              replace: true
            })
          }
        }
      )
    })
  }

  loadChatSessions = async () => {
    const conversations = await this.chatService.loadActiveConversations(true)
    const activeSessions: SessionItemData[] = []
    const demandSessions: SessionItemData[] = []

    conversations.forEach((conversation, chatId) => {
      const session = {
        id: chatId,
        lastMessageDateCreated:
          conversation.lastMessageDateCreated ?? Date.now(),
        topic: conversation.topic ?? undefined,
        demandId: conversation.demandId ?? undefined
      }

      if (conversation.demandId) {
        demandSessions.push(session)
      } else {
        activeSessions.push(session)
      }
    })

    this.setState({activeSessions, demandSessions}, () => {
      const sessionId = this.props.params.sessionId
      if (sessionId) {
        const sessionFound =
          this.state.activeSessions.find((s) => s.id === sessionId) ??
          this.state.demandSessions.find((s) => s.id === sessionId)
        if (sessionFound) {
          this.setState({selectedSession: sessionFound})
        } else {
          this.setState({selectedSession: this.createSessionItem(UUIDv4())})
          this.props.navigate('/chat', {replace: true})
        }
      }
    })
  }

  getInitSessionId(): string {
    if (
      this.props.params.sessionId &&
      !this.props.params.sessionId.startsWith(':')
    ) {
      return this.props.params.sessionId
    }
    return UUIDv4()
  }

  createSessionItem(id: string): SessionItemData {
    return {
      id: id,
      lastMessageDateCreated: Date.now()
    }
  }

  onStartSession = () => {
    const session = this.createSessionItem(UUIDv4())

    this.setState({
      selectedSession: session,
      isSessionsDrawerOpen: false,
      initMessage: undefined
    })

    this.props.navigate('/chat')
  }

  onSelectSession = (session: SessionItemData) => {
    this.setState({
      selectedSession: session,
      isSessionsDrawerOpen: false,
      initMessage: undefined
    })

    this.props.navigate(`/chat/${session.id}`)
  }

  onDeleteSession = async (session: SessionItemData) => {
    try {
      await this.chatService.deleteChat(session.id)
      const updatedSessions = this.state.activeSessions.filter(
        (s) => s.id !== session.id
      )

      this.setState(
        {
          activeSessions: updatedSessions,
          selectedSession: this.createSessionItem(UUIDv4())
        },
        () => {
          this.props.navigate(`/chat`)
        }
      )
    } catch (err) {
      console.error('Failed to delete chat session', err)
    }
  }

  toggleSessionDrawer = (open: boolean) => () =>
    this.setState({isSessionsDrawerOpen: open})

  renderPage() {
    return (
      <>
        {/* Main container */}
        <Box display="flex" flexDirection="column" height="100vh">
          {/* Desktop header: Visible only on desktop */}
          <Hidden {...responsiveBP.forMobile}>
            <Header />
          </Hidden>

          {/* Main content area: contains sidebar and chat area */}
          <Box flex={1} display="flex" overflow="hidden">
            {/* Sessions sidebar (desktop): Hidden on mobile */}
            <Hidden {...responsiveBP.forMobile}>
              {this.state.loading ? (
                /* Loading state shows skeleton */
                <SessionSkeleton />
              ) : this.state.activeSessions.length > 0 ||
                this.state.demandSessions.length > 0 ? (
                /* If sessions exist, show sidebar with session list */
                <SessionsSidebar
                  activeSessions={this.state.activeSessions}
                  demandSessions={this.state.demandSessions}
                  selectedSession={this.state.selectedSession}
                  onStartSession={this.onStartSession}
                  onSelectSession={(session) => this.onSelectSession(session)}
                  onDeleteSession={this.onDeleteSession}
                />
              ) : (
                /* If no sessions, show button to start a new chat */
                <CreateNewChatButton onClick={this.onStartSession} />
              )}
            </Hidden>

            {/* Session chat box: Main chat view */}
            <Box flex={1} overflow="auto" sx={{padding: {xs: 2, md: 0}}}>
              {/* Mobile header: only shown on mobile screens */}
              <Hidden {...responsiveBP.forDesktop}>
                <IconButton
                  edge="start"
                  onClick={this.toggleSessionDrawer(true)}
                  sx={{
                    borderRadius: 3,
                    padding: 1,
                    margin: 0,
                    backgroundColor: this.props.theme.palette.primary.main,
                    '&:hover': {
                      backgroundColor: this.props.theme.palette.primary.dark
                    }
                  }}
                >
                  <PasswordCheck
                    color={this.props.theme.palette.common.white}
                  />
                </IconButton>
              </Hidden>

              {/* Chat box: displays current chat session */}
              <SessionsChatBox
                key={this.state.selectedSession.id}
                session={this.state.selectedSession}
                isChatLocked={
                  this.state.selectedSession?.demandId ? true : false
                }
                initMessage={this.state.initMessage}
              />
            </Box>
          </Box>

          {/* Sessions drawer (mobile): slide-in drawer for chat sessions */}
          <Drawer
            anchor="left"
            open={this.state.isSessionsDrawerOpen}
            onClose={this.toggleSessionDrawer(false)}
            sx={{'& .MuiDrawer-paper': {width: 'auto'}}}
          >
            <Box onKeyDown={this.toggleSessionDrawer(false)}>
              {/* Drawer header: logo and close button */}
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                paddingX={2}
                paddingTop={1}
              >
                <Grid item flex="1 0 auto">
                  <Link to={HomeView.getPath()}>
                    <img alt="Finderella" src={appLogo} />
                  </Link>
                </Grid>
                <IconButton onClick={this.toggleSessionDrawer(false)}>
                  <CloseRoundedIcon />
                </IconButton>
              </Box>

              {/* Sessions sidebar (inside drawer): Use same logic as desktop sidebar */}
              {this.state.loading ? (
                <SessionSkeleton />
              ) : this.state.activeSessions.length > 0 ||
                this.state.demandSessions.length > 0 ? (
                <SessionsSidebar
                  activeSessions={this.state.activeSessions}
                  demandSessions={this.state.demandSessions}
                  selectedSession={this.state.selectedSession}
                  onStartSession={this.onStartSession}
                  onSelectSession={this.onSelectSession}
                  onDeleteSession={this.onDeleteSession}
                />
              ) : (
                <CreateNewChatButton onClick={this.onStartSession} />
              )}
            </Box>
          </Drawer>

          {/* Mobile header (bottom): fallback header on mobile */}
          <Hidden {...responsiveBP.forDesktop}>
            <Header />
          </Hidden>
        </Box>
      </>
    )
  }

  render() {
    return (
      <Loading
        loading={this.state.loading}
        render={this.renderPage.bind(this)}
      />
    )
  }
}

export default AssistantView.exportDefault()
